import {
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Grid,
  Group,
  NumberInput,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuArrowLeft, LuMinus, LuPackage, LuPlus, LuTrash2, LuTriangleAlert } from 'react-icons/lu'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import { useCanDelete, useCanWrite } from '@/frontend/hooks/useAuth'
import { useAdjustStock, useDeleteInventoryItem, useInventoryItem } from '@/frontend/hooks/useInventory'

export const Route = createFileRoute('/dashboard/facility/inventory/$id')({
  component: InventoryDetail,
})

function InventoryDetail() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const canWrite = useCanWrite()
  const canDelete = useCanDelete()
  const { data, isLoading } = useInventoryItem(id)
  const adjustMut = useAdjustStock()
  const deleteMut = useDeleteInventoryItem()

  const [delta, setDelta] = useState<number | ''>('')
  const [reason, setReason] = useState('')

  if (isLoading) {
    return (
      <Container size="lg" py="md">
        <Text c="dimmed">Loading…</Text>
      </Container>
    )
  }
  if (!data?.item) {
    return (
      <Container size="lg" py="md">
        <Stack gap="md" align="center" py="xl">
          <ThemeIcon variant="light" color="gray" size="xl" radius="xl">
            <LuTriangleAlert size={22} />
          </ThemeIcon>
          <Text fw={600}>Item tidak ditemukan</Text>
          <Button component={Link} to="/dashboard/facility/inventory" leftSection={<LuArrowLeft size={14} />}>
            Kembali ke daftar inventaris
          </Button>
        </Stack>
      </Container>
    )
  }

  const it = data.item
  const current = Number(it.currentStock)
  const low = Number(it.lowThreshold)
  const initial = Number(it.initialStock)

  const submitAdjust = (sign: 1 | -1) => {
    if (typeof delta !== 'number' || delta <= 0) {
      notifications.show({ color: 'orange', title: 'Masukkan jumlah', message: 'Jumlah harus lebih dari 0' })
      return
    }
    const signedDelta = sign * delta
    adjustMut.mutate(
      { id, data: { delta: signedDelta, reason: reason.trim() || undefined } },
      {
        onSuccess: () => {
          notifications.show({
            color: 'teal',
            title: 'Stok diperbarui',
            message: `${sign > 0 ? '+' : ''}${signedDelta} ${it.unit}`,
          })
          setDelta('')
          setReason('')
        },
        onError: (e) =>
          notifications.show({
            color: 'red',
            title: 'Gagal',
            message: (e as Error).message ?? 'Tidak dapat memperbarui',
          }),
      },
    )
  }

  const handleDelete = () => {
    modals.openConfirmModal({
      title: 'Hapus item inventaris?',
      children: (
        <Text size="sm">
          Item dan semua riwayat pergerakan stok akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.
        </Text>
      ),
      labels: { confirm: 'Hapus', cancel: 'Batal' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteMut.mutate(id, {
          onSuccess: () => {
            notifications.show({ color: 'teal', title: 'Terhapus', message: 'Item telah dihapus' })
            navigate({ to: '/dashboard/facility/inventory' })
          },
          onError: (e) =>
            notifications.show({
              color: 'red',
              title: 'Gagal',
              message: (e as Error).message ?? 'Tidak dapat menghapus',
            }),
        })
      },
    })
  }

  return (
    <Container size="lg" py="md">
      <Stack gap="lg">
        <Breadcrumbs>
          <Anchor component={Link} to="/dashboard/facility/inventory" c="dimmed">
            {t('nav.inventory', { defaultValue: 'Inventaris' })}
          </Anchor>
          <span>{it.name}</span>
        </Breadcrumbs>

        <Card padding="lg" radius="md" withBorder>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="sm" align="flex-start" wrap="nowrap">
              <ThemeIcon variant="light" color="laut" size="lg" radius="md">
                <LuPackage size={18} />
              </ThemeIcon>
              <div>
                <Title order={3} ff="var(--font-display)" fw={600} mb={4}>
                  {it.name}
                </Title>
                <Group gap="xs">
                  {it.category && (
                    <Badge variant="light" color="gray" radius="sm">
                      {it.category}
                    </Badge>
                  )}
                  {it.thresholdStatus && <StatusBadge status={it.thresholdStatus} kind="inventoryThreshold" upper />}
                </Group>
              </div>
            </Group>
            {canDelete && (
              <Button
                variant="subtle"
                color="red"
                size="xs"
                leftSection={<LuTrash2 size={14} />}
                onClick={handleDelete}
                loading={deleteMut.isPending}
              >
                Hapus
              </Button>
            )}
          </Group>
        </Card>

        <Grid>
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Card padding="lg" radius="md" withBorder>
              <Text fw={600} mb="md">
                Ringkasan Stok
              </Text>
              <Stack gap="md">
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                    Stok saat ini
                  </Text>
                  <Text
                    size="xl"
                    fw={700}
                    c={it.thresholdStatus === 'OUT' ? 'red.6' : it.thresholdStatus === 'LOW' ? 'orange.6' : undefined}
                  >
                    {current.toFixed(1)}{' '}
                    <Text component="span" size="sm" c="dimmed" fw={400}>
                      {it.unit}
                    </Text>
                  </Text>
                </Box>
                <Grid>
                  <Grid.Col span={6}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                      Ambang bawah
                    </Text>
                    <Text size="sm" fw={500}>
                      {low.toFixed(1)} {it.unit}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                      Stok awal
                    </Text>
                    <Text size="sm" fw={500}>
                      {initial.toFixed(1)} {it.unit}
                    </Text>
                  </Grid.Col>
                </Grid>
                {it.notes && (
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                      Catatan
                    </Text>
                    <Text size="sm">{it.notes}</Text>
                  </Box>
                )}
                <Text size="xs" c="dimmed">
                  Diperbarui {dayjs(it.updatedAt).format('DD MMM YYYY · HH:mm')}
                </Text>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 7 }}>
            <Stack gap="md">
              {canWrite && (
                <Card padding="lg" radius="md" withBorder>
                  <Text fw={600} mb="sm">
                    Sesuaikan Stok
                  </Text>
                  <Text size="xs" c="dimmed" mb="md">
                    Tambah saat penerimaan barang, kurangi saat pemakaian. Setiap perubahan tercatat sebagai pergerakan.
                  </Text>
                  <Stack gap="sm">
                    <Group gap="sm" align="flex-end" wrap="nowrap">
                      <NumberInput
                        label={`Jumlah (${it.unit})`}
                        placeholder="0"
                        value={delta}
                        onChange={(v) => setDelta(typeof v === 'number' ? v : '')}
                        min={0}
                        step={1}
                        decimalScale={2}
                        thousandSeparator=","
                        style={{ flex: 1 }}
                      />
                    </Group>
                    <TextInput
                      label="Alasan (opsional)"
                      placeholder="contoh: pemakaian harian, penerimaan supplier"
                      value={reason}
                      onChange={(e) => setReason(e.currentTarget.value)}
                    />
                    <Group gap="xs">
                      <Button
                        color="teal"
                        leftSection={<LuPlus size={14} />}
                        onClick={() => submitAdjust(1)}
                        loading={adjustMut.isPending}
                      >
                        Tambah Stok
                      </Button>
                      <Button
                        variant="light"
                        color="orange"
                        leftSection={<LuMinus size={14} />}
                        onClick={() => submitAdjust(-1)}
                        loading={adjustMut.isPending}
                        disabled={current <= 0}
                      >
                        Kurangi Stok
                      </Button>
                    </Group>
                  </Stack>
                </Card>
              )}

              <Card padding="lg" radius="md" withBorder>
                <Text fw={600} mb="sm">
                  Riwayat Pergerakan
                </Text>
                {!it.movements || it.movements.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    Belum ada pergerakan stok.
                  </Text>
                ) : (
                  <Table verticalSpacing="xs" highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Tanggal</Table.Th>
                        <Table.Th ta="right">Perubahan</Table.Th>
                        <Table.Th>Alasan</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {it.movements.map((m) => {
                        const d = Number(m.delta)
                        return (
                          <Table.Tr key={m.id}>
                            <Table.Td>
                              <Text size="sm">{dayjs(m.createdAt).format('DD MMM YYYY · HH:mm')}</Text>
                            </Table.Td>
                            <Table.Td ta="right">
                              <Text size="sm" fw={600} c={d > 0 ? 'teal.6' : 'orange.6'}>
                                {d > 0 ? '+' : ''}
                                {d.toFixed(1)} {it.unit}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" c={m.reason ? undefined : 'dimmed'}>
                                {m.reason ?? '—'}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        )
                      })}
                    </Table.Tbody>
                  </Table>
                )}
              </Card>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  )
}
